import * as React from 'react';
import { Plus, Upload } from 'lucide-react';
import { Sheet, SheetStat, WorkspaceButton, WorkspaceNotice } from '@/components/ui/workspace';
import { Spinner } from '@/components/ui/spinner';
import {
  UploadProgressBar,
  uploadPercent,
  uploadStatusLabel,
} from '@/components/ui/upload-progress';
import { cn } from '@/lib/utils/cn';
import { ApiError } from '@/lib/api/client';
import { newIdempotencyKey } from '@/lib/api/engagement-requirements';
import type {
  ConfirmImportInput,
  ImportOverrides,
  ImportPreview,
} from '@/lib/api/requirement-spreadsheets';
import type { UploadProgress, UploadProgressHandlers } from '@/lib/api/upload';
import { ImportReview } from './import-review';
import {
  IMPORT_ACCEPT,
  blockerMessage,
  canConfirmImport,
  duplicateRefs,
  fileProblem,
  importTotals,
  keptRefs,
  plural,
} from './import-logic';
import type { ModuleOption } from './module-select';

/**
 * "Import a spreadsheet" sheet (T-0153; E11-F06 and E11-F03).
 *
 * Reference: `openImport` / `renderImport` in `docs/requirement-library/
 * prototype/selection.js`. One surface with visible stages, not a wizard:
 * a drop zone, then the review of the file, with the running counts and
 * the confirm in the footer. Nothing is saved before the confirm.
 *
 * Several files can be dropped at once. Each is its own upload, preview
 * and confirm with its own status, so one that fails does not hold up the
 * others (E11-F06 Scenario 7). While a file is sent, its tab and the
 * reading panel show the bytes sent of the total, then "Processing…" from
 * the last byte until the server answers (T-0156). Cancelling the upload
 * aborts the request and drops the file from the list.
 *
 * The same sheet serves the engagement import and the library intake.
 * `target` supplies the three requests and the wording; the review is
 * `ImportReview` in both.
 *
 * Every change to a mapping previews again through the import token
 * (debounced, the file is not resent); the counts shown are always the
 * server's latest preview. Which likely duplicates to keep is held here
 * and sent with the confirm. The confirm carries an `Idempotency-Key`
 * that is reused when the same confirm is retried.
 */

export const PREVIEW_DEBOUNCE_MS = 350;

export interface ImportTarget<TResult> {
  variant: 'engagement' | 'library';
  /** Sheet title, for example "Import requirements". */
  title: string;
  /** Line under the title: where the rows go. */
  description: string;
  /** Active library modules, for unknown module names (engagement only). */
  modules?: ReadonlyArray<ModuleOption>;
  upload: (
    file: File,
    signal: AbortSignal,
    progress?: UploadProgressHandlers,
  ) => Promise<ImportPreview>;
  preview: (
    importToken: string,
    overrides: ImportOverrides,
    signal: AbortSignal,
  ) => Promise<ImportPreview>;
  confirm: (input: ConfirmImportInput) => Promise<TResult>;
  /** Label of the confirm button for `count` rows. */
  confirmLabel: (count: number) => string;
  /** One line for a file that has been imported, shown in its place. */
  describeResult: (result: TResult) => string;
  /** A sentence for a confirm error this target knows about, or null. */
  describeError?: (err: unknown) => string | null;
}

export interface SpreadsheetImportSheetProps<TResult> {
  target: ImportTarget<TResult>;
  onClose: () => void;
  /** One file was imported. Called once per file. */
  onImported: (result: TResult) => void;
}

type EntryStatus = 'reading' | 'review' | 'failed' | 'imported';

interface FileEntry {
  id: string;
  name: string;
  size: number;
  status: EntryStatus;
  /** Why the file could not be read. */
  error: string | null;
  /** Bytes sent while `reading`; null before the first progress event. */
  upload: UploadProgress | null;
  /** Every byte has been sent and the server is reading the file. */
  processing: boolean;
  preview: ImportPreview | null;
  overrides: ImportOverrides;
  keep: ReadonlySet<string>;
  acknowledgedRepeat: boolean;
  refreshing: boolean;
  confirming: boolean;
  /** The last re-preview or confirm failure. */
  reviewError: string | null;
  doneMessage: string | null;
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function conflictReason(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.code !== 'conflict') return null;
  const reason = err.details?.['reason'];
  return typeof reason === 'string' ? reason : null;
}

const STATUS_LABEL: Record<EntryStatus, string> = {
  reading: 'Reading',
  review: 'To review',
  failed: 'Could not be read',
  imported: 'Imported',
};

const STATUS_TONE: Record<EntryStatus, string> = {
  reading: 'bg-ink-100 text-ink-700',
  review: 'bg-posture-at-risk-tint text-posture-at-risk',
  failed: 'bg-posture-critical-tint text-posture-critical',
  imported: 'bg-posture-healthy-tint text-posture-healthy',
};

export function SpreadsheetImportSheet<TResult>({
  target,
  onClose,
  onImported,
}: SpreadsheetImportSheetProps<TResult>) {
  const [entries, setEntries] = React.useState<FileEntry[]>([]);
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [dragOver, setDragOver] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // The latest state and props, for the timers and the request callbacks.
  const entriesRef = React.useRef(entries);
  entriesRef.current = entries;
  const targetRef = React.useRef(target);
  targetRef.current = target;
  const onImportedRef = React.useRef(onImported);
  onImportedRef.current = onImported;
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  const seq = React.useRef(0);
  const timers = React.useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const requests = React.useRef(new Map<string, { n: number; controller: AbortController }>());
  const idempotency = React.useRef(new Map<string, { signature: string; key: string }>());

  React.useEffect(() => {
    const pendingTimers = timers.current;
    const pendingRequests = requests.current;
    return () => {
      pendingTimers.forEach((t) => clearTimeout(t));
      pendingRequests.forEach((r) => r.controller.abort());
    };
  }, []);

  const patch = React.useCallback(
    (id: string, change: Partial<FileEntry> | ((entry: FileEntry) => Partial<FileEntry>)) => {
      setEntries((prev) =>
        prev.map((e) =>
          e.id === id ? { ...e, ...(typeof change === 'function' ? change(e) : change) } : e,
        ),
      );
    },
    [],
  );

  /** Start a request for one file; an earlier one for the same file is dropped. */
  const begin = (id: string) => {
    requests.current.get(id)?.controller.abort();
    seq.current += 1;
    const request = { n: seq.current, controller: new AbortController() };
    requests.current.set(id, request);
    return {
      signal: request.controller.signal,
      isCurrent: () => requests.current.get(id)?.n === request.n,
    };
  };

  const addFiles = (files: ReadonlyArray<File>) => {
    if (files.length === 0) return;
    const added: FileEntry[] = files.map((file) => {
      seq.current += 1;
      const problem = fileProblem(file);
      return {
        id: `file-${seq.current}`,
        name: file.name,
        size: file.size,
        status: problem ? 'failed' : 'reading',
        error: problem,
        upload: null,
        processing: false,
        preview: null,
        overrides: {},
        keep: new Set<string>(),
        acknowledgedRepeat: false,
        refreshing: false,
        confirming: false,
        reviewError: null,
        doneMessage: null,
      };
    });
    setEntries((prev) => [...prev, ...added]);
    setActiveId((current) => {
      const stillThere = entriesRef.current.find((e) => e.id === current);
      if (stillThere && stillThere.status !== 'imported' && stillThere.status !== 'failed') {
        return current;
      }
      return (added.find((e) => e.status === 'reading') ?? added[0]!).id;
    });
    added.forEach((entry, i) => {
      if (entry.status !== 'reading') return;
      const { signal, isCurrent } = begin(entry.id);
      const progress: UploadProgressHandlers = {
        onProgress: (next) => {
          if (!isCurrent()) return;
          // Only a new whole percent is worth a render.
          const shown = entriesRef.current.find((e) => e.id === entry.id)?.upload ?? null;
          if (shown && uploadPercent(shown) === uploadPercent(next)) return;
          patch(entry.id, { upload: next });
        },
        onUploaded: () => {
          if (isCurrent()) patch(entry.id, { processing: true });
        },
      };
      targetRef.current.upload(files[i]!, signal, progress).then(
        (preview) => {
          if (isCurrent()) patch(entry.id, { status: 'review', preview });
        },
        (err: unknown) => {
          if (!isCurrent()) return;
          patch(entry.id, {
            status: 'failed',
            error: messageOf(err, 'The file could not be read.'),
          });
        },
      );
    });
  };

  const runPreview = (id: string) => {
    timers.current.delete(id);
    const entry = entriesRef.current.find((e) => e.id === id);
    if (!entry?.preview || entry.status !== 'review') return;
    const { signal, isCurrent } = begin(id);
    targetRef.current.preview(entry.preview.import_token, entry.overrides, signal).then(
      (preview) => {
        if (!isCurrent()) return;
        const flagged = new Set(duplicateRefs(preview));
        patch(id, (e) => ({
          preview,
          refreshing: false,
          reviewError: null,
          // A row that is no longer flagged has nothing to keep.
          keep: new Set(Array.from(e.keep).filter((ref) => flagged.has(ref))),
        }));
      },
      (err: unknown) => {
        if (!isCurrent()) return;
        patch(id, {
          refreshing: false,
          reviewError: `The preview could not be updated: ${messageOf(err, 'the change was refused.')}`,
        });
      },
    );
  };

  const changeOverrides = (id: string, change: (o: ImportOverrides) => ImportOverrides) => {
    patch(id, (e) => ({ overrides: change(e.overrides), refreshing: true, reviewError: null }));
    const pending = timers.current.get(id);
    if (pending) clearTimeout(pending);
    timers.current.set(
      id,
      setTimeout(() => runPreview(id), PREVIEW_DEBOUNCE_MS),
    );
  };

  const setKeep = (id: string, refs: ReadonlyArray<string>, keep: boolean) => {
    patch(id, (e) => {
      const next = new Set(e.keep);
      refs.forEach((ref) => (keep ? next.add(ref) : next.delete(ref)));
      return { keep: next };
    });
  };

  const removeEntry = (id: string) => {
    const pending = timers.current.get(id);
    if (pending) clearTimeout(pending);
    timers.current.delete(id);
    requests.current.get(id)?.controller.abort();
    requests.current.delete(id);
    const rest = entriesRef.current.filter((e) => e.id !== id);
    setEntries(rest);
    setActiveId((current) => (current === id ? (rest[0]?.id ?? null) : current));
  };

  const confirm = (entry: FileEntry) => {
    const preview = entry.preview;
    if (!preview || entry.confirming || entry.refreshing) return;
    if (!canConfirmImport(preview, entry.keep, entry.acknowledgedRepeat)) return;
    const input = {
      importToken: preview.import_token,
      keepDuplicateRefs: keptRefs(preview, entry.keep),
      acknowledgeRepeat: preview.repeat_of != null && entry.acknowledgedRepeat,
    };
    // One key per distinct confirm; a retry of the same confirm reuses it.
    const signature = JSON.stringify(input);
    let key = idempotency.current.get(entry.id);
    if (key?.signature !== signature) {
      key = { signature, key: newIdempotencyKey() };
      idempotency.current.set(entry.id, key);
    }
    patch(entry.id, { confirming: true, reviewError: null });
    targetRef.current.confirm({ ...input, idempotencyKey: key.key }).then(
      (result) => {
        patch(entry.id, {
          status: 'imported',
          confirming: false,
          doneMessage: targetRef.current.describeResult(result),
        });
        onImportedRef.current(result);
        const next = entriesRef.current.find(
          (e) => e.id !== entry.id && (e.status === 'review' || e.status === 'reading'),
        );
        if (next) setActiveId(next.id);
        else onCloseRef.current();
      },
      (err: unknown) => {
        const reason = conflictReason(err);
        // The server answered, so this attempt is over: the next confirm is
        // a new one. Only a lost response is retried under the same key.
        if (!(err instanceof ApiError) || err.code !== 'network') {
          idempotency.current.delete(entry.id);
        }
        let message = targetRef.current.describeError?.(err) ?? null;
        if (!message && reason === 'stale_token') {
          message = 'The preview had changed. It has been refreshed: check it, then confirm again.';
        } else if (!message && reason === 'import_expired') {
          message = 'This preview is more than a day old. Choose the file again.';
        }
        patch(entry.id, {
          confirming: false,
          reviewError:
            message ?? messageOf(err, 'The file could not be imported. Nothing was saved.'),
        });
        if (reason === 'stale_token') {
          patch(entry.id, { refreshing: true });
          runPreview(entry.id);
        }
      },
    );
  };

  const active = entries.find((e) => e.id === activeId) ?? entries[0] ?? null;
  const working = entries.some((e) => e.confirming);
  const unfinished = entries.some((e) => e.status === 'review' || e.status === 'reading');

  const preview = active?.status === 'review' ? active.preview : null;
  const totals = preview && active ? importTotals(preview, active.keep) : null;
  const blocker = preview ? blockerMessage(preview) : null;
  const canConfirm =
    preview != null &&
    active != null &&
    !active.refreshing &&
    !active.confirming &&
    canConfirmImport(preview, active.keep, active.acknowledgedRepeat);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(Array.from(e.dataTransfer?.files ?? []));
  };

  return (
    <Sheet
      open
      onOpenChange={(next) => {
        if (!next && !working) onClose();
      }}
      closeOnScrimClick={!unfinished}
      onEscapeKeyDown={(e) => {
        // Esc closes an open module list first, not the whole sheet.
        if (document.querySelector('[data-import-popup-open]')) e.preventDefault();
      }}
      title={target.title}
      description={target.description}
      summary={
        totals && preview ? (
          <>
            <SheetStat value={totals.toImport.toLocaleString()} label="to import" />
            <SheetStat
              value={totals.duplicatesSkipped.toLocaleString()}
              label={totals.duplicatesSkipped === 1 ? 'duplicate skipped' : 'duplicates skipped'}
            />
            <SheetStat
              value={(totals.excluded + totals.refused).toLocaleString()}
              label="excluded"
            />
            {blocker ? (
              <span className="text-posture-critical" role="status">
                {blocker}
              </span>
            ) : preview.repeat_of && !active?.acknowledgedRepeat ? (
              <span className="text-posture-at-risk" role="status">
                Tick &ldquo;Import this file again&rdquo; to continue.
              </span>
            ) : null}
          </>
        ) : undefined
      }
      actions={
        <>
          <WorkspaceButton size="md" disabled={working} onClick={onClose}>
            {entries.some((e) => e.status === 'imported') ? 'Close' : 'Cancel'}
          </WorkspaceButton>
          {preview && totals ? (
            <WorkspaceButton
              size="md"
              variant="primary"
              disabled={!canConfirm}
              onClick={() => active && confirm(active)}
            >
              {active?.confirming ? 'Importing…' : target.confirmLabel(totals.toImport)}
            </WorkspaceButton>
          ) : null}
        </>
      }
    >
      <div
        data-testid="import-body"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto bg-paper-50 p-5 [&>*]:shrink-0"
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={IMPORT_ACCEPT}
          aria-label="Choose spreadsheets"
          className="sr-only"
          onChange={(e) => {
            addFiles(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />

        {entries.length === 0 ? (
          <div
            className={cn(
              'flex flex-1 flex-col items-center justify-center gap-3 rounded-large border-[1.5px] border-dashed p-10 text-center',
              dragOver ? 'border-accent-amber-600 bg-accent-amber-50' : 'border-ink-200 bg-paper-0',
            )}
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-card bg-paper-100 text-ink-700">
              <Upload className="h-[18px] w-[18px]" aria-hidden="true" />
            </span>
            <div className="text-h3 font-semibold text-ink-900">Drop spreadsheets here</div>
            <p className="max-w-[460px] text-meta font-medium text-ink-500">
              .xlsx, .xls, .xlsm or .csv, one or several, up to 20 MB and 10,000 rows each. Cover
              and legend tabs are skipped, banner rows above the headers are ignored, and a workbook
              with one tab per module uses the tab name as the module.
            </p>
            <WorkspaceButton size="md" variant="primary" onClick={() => inputRef.current?.click()}>
              Choose files
            </WorkspaceButton>
            <p className="text-meta text-ink-500">
              {target.variant === 'library'
                ? 'Rows land in the draft, unpublished. The original file is not kept.'
                : 'Nothing is saved until you confirm. Then the file is kept in Documents, where the imported requirements cite it.'}
            </p>
          </div>
        ) : (
          <>
            <div
              role="tablist"
              aria-label="Files"
              className={cn(
                'flex flex-wrap items-center gap-1.5',
                dragOver && 'rounded-card outline outline-2 outline-accent-amber-600',
              )}
            >
              {entries.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  role="tab"
                  aria-selected={active?.id === e.id}
                  onClick={() => setActiveId(e.id)}
                  className={cn(
                    'inline-flex h-8 max-w-[320px] items-center gap-2 rounded-card border px-2.5 text-meta font-medium',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 focus-visible:ring-offset-1',
                    active?.id === e.id
                      ? 'border-ink-900 bg-paper-0 text-ink-900'
                      : 'border-ink-200 bg-paper-0 text-ink-700 hover:border-ink-300',
                  )}
                >
                  <span className="min-w-0 truncate">{e.name}</span>
                  <span
                    className={cn(
                      'inline-flex h-5 flex-none items-center rounded-full px-2 text-[11px]',
                      STATUS_TONE[e.status],
                    )}
                  >
                    {e.status === 'reading'
                      ? uploadStatusLabel(e.upload, e.processing)
                      : STATUS_LABEL[e.status]}
                  </span>
                </button>
              ))}
              <WorkspaceButton
                variant="tertiary"
                disabled={working}
                onClick={() => inputRef.current?.click()}
              >
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                Add files
              </WorkspaceButton>
            </div>

            {active?.status === 'reading' ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-large border border-ink-100 bg-paper-0 p-10 text-center text-body-sm text-ink-500">
                <div role="status" className="flex flex-col items-center gap-2">
                  <Spinner />
                  <span className="font-medium text-ink-900">Reading {active.name}…</span>
                  Finding the sheets, the header rows and the columns.
                </div>
                <UploadProgressBar
                  className="max-w-[360px]"
                  fileName={active.name}
                  progress={active.upload}
                  processing={active.processing}
                />
                <WorkspaceButton className="mt-1" onClick={() => removeEntry(active.id)}>
                  Cancel upload
                </WorkspaceButton>
              </div>
            ) : null}

            {active?.status === 'failed' ? (
              <WorkspaceNotice tone="critical">
                <div className="font-semibold text-ink-900">{active.name} was not imported</div>
                <p className="mt-0.5">{active.error}</p>
                <div className="mt-2">
                  <WorkspaceButton onClick={() => removeEntry(active.id)}>
                    Remove from the list
                  </WorkspaceButton>
                </div>
              </WorkspaceNotice>
            ) : null}

            {active?.status === 'imported' ? (
              <WorkspaceNotice>
                <div className="font-semibold text-ink-900">{active.name} was imported</div>
                <p className="mt-0.5">{active.doneMessage}</p>
              </WorkspaceNotice>
            ) : null}

            {active && preview ? (
              <ImportReview
                // Another file is another review: its tab and paging start clean.
                key={active.id}
                variant={target.variant}
                preview={preview}
                keep={active.keep}
                acknowledgedRepeat={active.acknowledgedRepeat}
                refreshing={active.refreshing}
                disabled={active.confirming}
                modules={target.modules ?? []}
                error={active.reviewError}
                onOverrides={(change) => changeOverrides(active.id, change)}
                onKeep={(refs, keep) => setKeep(active.id, refs, keep)}
                onAcknowledgeRepeat={(acknowledged) =>
                  patch(active.id, { acknowledgedRepeat: acknowledged })
                }
                onReplace={() => {
                  removeEntry(active.id);
                  inputRef.current?.click();
                }}
              />
            ) : null}
          </>
        )}
      </div>
    </Sheet>
  );
}
